import { ReactNode, SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useSingleModal } from '../../../hooks/useSingleModal';
import { FilterMenuWithPasteValuesButton } from '../FilterMenuWithPasteValuesButton';
import { FilterPasteModal } from '../FilterPasteModal';

type SearchEvent = SyntheticEvent;

export type UsePasteValuesMenuProps = {
  enabled: boolean;
  search?: string;
  modalText?: string;
  onSearch?: (query: string, event: SearchEvent) => void;
  onConfirm?: (query: string) => void;
};

/**
 * Botão "Colar lista de códigos" + modal, no mesmo formato do CheckPicker do Gerenciador de Preços.
 * Para usar em pickers que não são o components/CheckPicker: o picker passa a usar `searchable={false}`,
 * `onSearch={handleSearch}` e `renderMenu={renderMenu}`, e renderiza o `modal`.
 */
export const usePasteValuesMenu = ({ enabled, search, modalText, onSearch, onConfirm }: UsePasteValuesMenuProps) => {
  const [query, setQuery] = useState(search ?? '');
  const [modalQuery, setModalQuery] = useState('');
  const [isOpenModal, { open: openModal, close: closeModal }] = useSingleModal();

  const handleSearch = useCallback(
    (query: string, event: SearchEvent) => {
      setQuery(query);
      onSearch?.(query, event);
    },
    [onSearch],
  );

  const handleConfirm = useCallback(
    (query: string) => {
      onConfirm?.(query);
      closeModal();
      setModalQuery('');
    },
    [onConfirm, closeModal],
  );

  const renderMenu = useCallback(
    (menu: ReactNode) => {
      if (!enabled) return menu;
      return <FilterMenuWithPasteValuesButton menu={menu} search={query} handleSearch={handleSearch} handleOpenModal={openModal} />;
    },
    [enabled, query, handleSearch, openModal],
  );

  useEffect(() => setQuery(search ?? ''), [search]);

  const modal = enabled ? (
    <FilterPasteModal show={isOpenModal} query={modalQuery} onHide={closeModal} onSearch={setModalQuery} onConfirm={handleConfirm} modalText={modalText} />
  ) : null;

  return { handleSearch, renderMenu, modal } as const;
};
